import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from "react";
import { DEPOSIT_METHODS } from "../components/Pages/Deposit/depositMethods";
// Outside a provider (the regular swap flow) `actionButtonText` is undefined so
// consumers apply their own fallback (e.g. "Swap now").
const CONTEXT_DEFAULTS = {
    showDestinationAddress: true,
    actionButtonText: undefined,
    defaultAmountUsd: 1,
    methods: [...DEPOSIT_METHODS],
    isDepositFlow: false,
};
// The deposit flow's own default label. Lives here as the single source of
// truth — DepositCard passes the integrator's raw prop and this fills the gap.
const DEPOSIT_ACTION_BUTTON_TEXT = "Deposit";
const DepositSettingsContext = createContext(CONTEXT_DEFAULTS);
/**
 * Per-instance deposit configuration. Replaces the old `DepositSettings` mutable
 * statics, which were a global singleton — two `<Deposit>` widgets on the same
 * page would stomp on each other's config, and writing them during render
 * violated React's rules. Scoping the values to a Context makes each widget
 * instance independent.
 */
export function DepositSettingsProvider({ value, children, }) {
    const merged = useMemo(() => ({
        showDestinationAddress: value.showDestinationAddress ?? false,
        actionButtonText: value.actionButtonText || DEPOSIT_ACTION_BUTTON_TEXT,
        defaultAmountUsd: value.defaultAmountUsd ?? 1,
        methods: value.methods ?? [...DEPOSIT_METHODS],
        isDepositFlow: true,
    }), [value.showDestinationAddress, value.actionButtonText, value.defaultAmountUsd, value.methods]);
    return (_jsx(DepositSettingsContext.Provider, { value: merged, children: children }));
}
/**
 * Reads the current deposit settings. Returns defaults when used outside a
 * `DepositSettingsProvider` (e.g. the regular swap flow), so shared components
 * like the quote summary and submit button work in both contexts.
 */
export function useDepositSettings() {
    return useContext(DepositSettingsContext);
}
